import z from 'zod';

import {
  BaseReferenceDto,
  PlanResourceReference,
  SearchDocument,
  Url,
} from '@cfa/pathway-fe-shared/xp';

export const isPlanResourceReference = (
  reference: BaseReferenceDto,
): reference is PlanResourceReference =>
  z
    .object({
      urls: z.array(z.unknown()),
    })
    .safeParse(reference).success;

/** Pick the URL entry that matches the reference language, like itemByLanguage on references. */
export const resolvePlanResourceUrl = (
  reference: PlanResourceReference,
): Url | undefined => {
  const byLanguage = reference.urls.find(
    url => url.language.toLowerCase() === reference.language.toLowerCase(),
  );

  return byLanguage ?? reference.urls.at(0);
};

/** Known SearchDocument.type values from XP search results. */
const searchDocumentTypeSchema = z.enum([
  'BRIGHTCOVE',
  'LEARNUPON',
  'PROCEDURE',
  'JOB_AID',
  'GAME',
  'ORG',
  'QUALITY_PHOTOS',
  'VIDEO',
  'PDF',
  'OPERATIONAL_REQUIREMENTS',
  'PODCAST',
  'SIMULATOR',
  'ELEARNING',
]);

/** Plan steps store type on resource.type; reference.format can differ (e.g. PROCEDURE vs PDF). */
export const resolvePlanResourceDocumentType = (
  resourceType: string | undefined,
  format: string,
): SearchDocument['type'] | undefined => {
  const parsed = searchDocumentTypeSchema.safeParse(
    (resourceType ?? format).toUpperCase(),
  );

  return parsed.success ? parsed.data : undefined;
};

/** Prefer contentUrl unless it is empty; fall back to contentApiUrl (matches SearchResults Tridion logic). */
const resolveContentUrl = (primaryUrl: Url | undefined): string | undefined => {
  if (primaryUrl?.contentUrl) {
    return primaryUrl.contentUrl;
  }

  return primaryUrl?.contentApiUrl;
};

export const resolvePlanResourceContentUrl = (
  reference: PlanResourceReference,
): string | undefined => resolveContentUrl(resolvePlanResourceUrl(reference));

export const mapReferenceToDocument = (
  reference: PlanResourceReference,
  resourceType?: string,
): SearchDocument => {
  const primaryUrl = resolvePlanResourceUrl(reference);

  return {
    contentApiUrl: primaryUrl?.contentApiUrl,
    contentUrl: resolveContentUrl(primaryUrl),
    icon: undefined,
    id: reference.id,
    lastModified: reference.updatedAt,
    name: reference.name,
    otherContentUrl: undefined,
    otherName: undefined,
    otherPrintUrl: undefined,
    printUrl: primaryUrl?.printUrl ?? undefined,
    searchId: '',
    sourceSystem: reference.sourceSystem as SearchDocument['sourceSystem'],
    tags: reference.tags,
    type: resolvePlanResourceDocumentType(resourceType, reference.format),
  };
};
